// One round. The headline breaks; ten seconds to go long, short or sit out; then the next hour of
// the market plays in fifteen seconds, the first minutes in slow motion, the bust waiting where
// papertrade would put it. Close whenever you like. Every figure that counts comes from the shared
// settle(), the same code that scores the leaderboard; the chart and the ticking number are for
// the eye.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { bufferOf, bustPrice, dollars, isBust, percent, priceText, toFixed, toNumber } from "../../shared/game/money.ts";
import { Burst } from "./Burst";
import { fx, haptic } from "./fx";
import { bestOf, CONTEXT_MINUTES, hourOf, LEVERAGES, POINTS_PER_MINUTE, REPLAY_MINUTES, replayPath, settle, type Round, type Side } from "./rounds";
import { useWidth } from "./useWidth";

export { LEVERAGES };
export type Leverage = (typeof LEVERAGES)[number];

/** Seconds to decide. */
const DECIDE_S = 10;
/** The first minutes after the headline play slower: that is where the news is priced. */
const SLOW_MINUTES = 3;
const SLOW_MS_PER_MINUTE = 700;
/** The hour after the headline, played in this long. */
const REPLAY_MS = 15_000;
const FAST_MS_PER_MINUTE = (REPLAY_MS - SLOW_MINUTES * SLOW_MS_PER_MINUTE) / (REPLAY_MINUTES - SLOW_MINUTES);

/** When each path point is reached, ms from the start of the replay. */
function timeline(points: number): number[] {
  const out: number[] = [];
  let at = 0;
  for (let i = 0; i < points; i++) {
    out.push(at);
    const minute = Math.floor(i / POINTS_PER_MINUTE);
    at += (minute < SLOW_MINUTES ? SLOW_MS_PER_MINUTE : FAST_MS_PER_MINUTE) / POINTS_PER_MINUTE;
  }
  return out;
}

/** Where the replay is at `elapsed` ms, as a fractional path index. */
function progressAt(times: number[], elapsed: number): number {
  let lo = 0;
  let hi = times.length - 1;
  if (elapsed >= (times[hi] as number)) return hi;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if ((times[mid] as number) <= elapsed) lo = mid;
    else hi = mid;
  }
  const a = times[lo] as number;
  const b = times[lo + 1] as number;
  return lo + (elapsed - a) / (b - a || 1);
}

export type Outcome = {
  side: Side | null;
  leverage: number;
  pnl: bigint;
  busted: boolean;
  /** The path point the player closed at, null if they held or sat out: the decision as scored. */
  closedAt: number | null;
};

/** How each kind of news has gone in the archive: hours that ended lower, of all. */
export type Archive = Map<string, { down: number; of: number }>;

type Phase = "decide" | "replay" | "result";

const dateOf = (ms: number) =>
  new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).toUpperCase() +
  ` · ${new Date(ms).toISOString().slice(11, 16)} UTC`;

const placesOf = (fixed: bigint) => (fixed >= 1_000n * 10n ** 8n ? 1 : fixed >= 10n * 10n ** 8n ? 2 : 4);

type Props = {
  round: Round;
  last: boolean;
  leverage: Leverage;
  margin: bigint;
  archive?: Archive | undefined;
  onLeverage: (leverage: Leverage) => void;
  onDone: (outcome: Outcome) => void;
  /** More actions beside Next on the result, given the outcome (survival's walk away). */
  aside?: ((outcome: Outcome) => ReactNode) | undefined;
};

export function Stage({ round, last, leverage, margin, archive, onLeverage, onDone, aside }: Props) {
  const [phase, setPhase] = useState<Phase>("decide");
  const [side, setSide] = useState<Side | null>(null);
  const [left, setLeft] = useState(DECIDE_S);
  const [shown, setShown] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [burst, setBurst] = useState<{ fire: number; tone: "up" | "down" }>({ fire: 0, tone: "up" });
  const settled = useRef(false);
  const born = useRef(performance.now());
  const leverageNow = useRef(leverage);
  leverageNow.current = leverage;

  const path = useMemo(() => replayPath(round), [round]);
  const times = useMemo(() => timeline(path.length), [path.length]);
  const entry = path[0] ?? toFixed((round.bars[round.headlineBar] as [string, string, string, string])[0]);
  const context = useMemo(() => {
    const from = Math.max(0, round.headlineBar - CONTEXT_MINUTES);
    return round.bars.slice(from, round.headlineBar).map((bar) => toNumber(toFixed(bar[3])));
  }, [round]);
  const bust = side ? bustPrice(entry, leverage, side === "long", bufferOf(round.symbol)) : null;
  const places = placesOf(entry);

  const finish = useCallback(
    (decision: { side: Side | null; leverage: number; closedAt: number | null }) => {
      if (settled.current) return;
      settled.current = true;
      const result = settle(round, decision, margin);
      const done: Outcome = { ...decision, pnl: result.pnl, busted: result.busted };
      setOutcome(done);
      setPhase("result");
      setShown(result.exitAt ?? path.length - 1);
      if (result.busted) {
        fx.bust();
        haptic([60, 40, 140]);
        setBurst({ fire: Date.now(), tone: "down" });
      } else if (decision.side && result.pnl > 0n) {
        const size = Math.min(1, Number(result.pnl) / Number(margin) / 3);
        fx.win(size);
        if (result.pnl >= margin) setBurst({ fire: Date.now(), tone: "up" });
      } else if (decision.side) fx.lose();
    },
    [round, margin, path.length],
  );

  // the headline lands
  useEffect(() => {
    fx.headline();
  }, []);

  // the clock while deciding, from the moment the headline broke: out of time is sitting it out
  useEffect(() => {
    if (phase !== "decide") return;
    let lastBeep = DECIDE_S;
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, DECIDE_S - (performance.now() - born.current) / 1000);
      setLeft(remaining);
      const whole = Math.ceil(remaining);
      if (whole <= 3 && whole < lastBeep && whole > 0) {
        lastBeep = whole;
        fx.countdown(whole);
      }
      if (remaining <= 0) {
        window.clearInterval(timer);
        finish({ side: null, leverage: leverageNow.current, closedAt: null });
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [phase, finish]);

  // the replay: points revealed against the clock, the bust checked at every one
  useEffect(() => {
    if (phase !== "replay" || !side || bust === null) return;
    const long = side === "long";
    const started = performance.now();
    let checked = 0;
    let frame = 0;
    let lastValue = 0;
    const tick = (now: number) => {
      const at = progressAt(times, now - started);
      const reached = Math.floor(at);
      for (let i = checked + 1; i <= reached; i++) {
        const price = path[i] as bigint;
        if (isBust(price, bust, long)) {
          finish({ side, leverage, closedAt: null });
          return;
        }
        const value = (long ? 1 : -1) * leverage * (toNumber(price) / toNumber(entry) - 1);
        fx.tick(value - lastValue);
        lastValue = value;
      }
      checked = reached;
      setShown(at);
      if (reached >= path.length - 1) {
        finish({ side, leverage, closedAt: null });
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [phase, side, bust, path, times, leverage, entry, finish]);

  const choose = useCallback(
    (chosen: Side) => {
      if (phase !== "decide") return;
      fx.open(chosen);
      haptic(12);
      setSide(chosen);
      setPhase("replay");
    },
    [phase],
  );

  const close = useCallback(() => {
    if (phase !== "replay" || !side) return;
    fx.close();
    finish({ side, leverage, closedAt: Math.floor(shown) });
  }, [phase, side, shown, leverage, finish]);

  const skip = useCallback(() => {
    if (phase !== "decide") return;
    finish({ side: null, leverage, closedAt: null });
  }, [phase, leverage, finish]);

  // keys: L long, S short, X sit out, 1 to 4 leverage, space to close, enter for the next one
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const key = event.key.toLowerCase();
      if (phase === "decide") {
        if (key === "l" || key === "arrowup") choose("long");
        else if (key === "s" || key === "arrowdown") choose("short");
        else if (key === "x") skip();
        else if (/^[1-4]$/.test(key)) onLeverage(LEVERAGES[Number(key) - 1] as Leverage);
        else return;
      } else if (phase === "replay" && (key === " " || key === "c")) close();
      else if (phase === "result" && (key === "enter" || key === " ") && outcome) onDone(outcome);
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, choose, skip, close, onLeverage, onDone, outcome]);

  // what the trade is worth right now, for the eye
  const at = Math.floor(shown);
  const fraction = shown - at;
  const nowPrice = toNumber(path[at] ?? entry) + (toNumber(path[Math.min(path.length - 1, at + 1)] ?? entry) - toNumber(path[at] ?? entry)) * fraction;
  const entryNumber = toNumber(entry);
  const liveCents =
    side && phase === "replay"
      ? Math.max(-Number(margin), (Number(margin) * leverage * (side === "long" ? nowPrice - entryNumber : entryNumber - nowPrice)) / entryNumber)
      : 0;
  const minute = Math.min(REPLAY_MINUTES, Math.floor(shown / POINTS_PER_MINUTE));
  const slow = phase === "replay" && minute < SLOW_MINUTES;

  const hour = hourOf(round);
  const best = phase === "result" ? bestOf(round, outcome?.leverage ?? leverage, margin) : null;
  const record = archive?.get(round.category);

  return (
    <section className={`gm-stage is-${phase} ${outcome?.busted ? "is-busted" : ""} ${slow ? "is-slow" : ""}`}>
      <div className="gm-when num">{dateOf(round.at)}</div>
      <h1 className="gm-headline" key={round.id}>
        {round.headline}
      </h1>
      <div className="gm-market">
        <span className="gm-symbol">{round.symbol}</span>
        <span className="num">{priceText(entry, places)}</span>
        {round.source && <span className="gm-source">{round.source}</span>}
      </div>

      <Chart
        context={context}
        path={path}
        shown={phase === "decide" ? -1 : shown}
        entry={entryNumber}
        bust={bust === null ? null : toNumber(bust)}
        side={side}
        winning={phase === "result" ? (outcome?.pnl ?? 0n) > 0n : liveCents > 0}
      />

      {phase === "decide" && (
        <div className="gm-decide">
          <div className="gm-clock" aria-hidden>
            <span style={{ transform: `scaleX(${left / DECIDE_S})` }} className={left <= 3 ? "is-late" : ""} />
          </div>
          <div className="gm-levers" role="radiogroup" aria-label="Leverage">
            {LEVERAGES.map((option, index) => (
              <button key={option} type="button" role="radio" aria-checked={option === leverage} className="gm-lever num" onClick={() => onLeverage(option)} title={`Key ${index + 1}`}>
                {option}×
              </button>
            ))}
          </div>
          <div className="gm-sides">
            <button type="button" className="gm-side gm-long" onClick={() => choose("long")} title="L">
              Long
            </button>
            <button type="button" className="gm-side gm-short" onClick={() => choose("short")} title="S">
              Short
            </button>
          </div>
          <button type="button" className="gm-skip" onClick={skip}>
            Sit this one out
          </button>
        </div>
      )}

      {phase === "replay" && side && (
        <div className="gm-replay">
          <div className={`gm-pnl num ${liveCents > 0 ? "up" : liveCents < 0 ? "down" : ""}`}>{dollars(BigInt(Math.trunc(liveCents)), true)}</div>
          <div className="gm-replay-meta num">
            {side === "long" ? "Long" : "Short"} {leverage}× · +{minute} min{slow ? " · slow motion" : ""}
          </div>
          <button type="button" className="gm-close" onClick={close}>
            Close
          </button>
        </div>
      )}

      {phase === "result" && outcome && (
        <div className="gm-result">
          <div className={`gm-pnl num ${outcome.pnl > 0n ? "up" : outcome.pnl < 0n ? "down" : ""}`}>
            {outcome.busted ? "Busted" : outcome.side ? dollars(outcome.pnl, true) : "Sat out"}
          </div>
          <div className="gm-result-meta">
            {round.symbol} {percent(hour.from, hour.to)} in the hour
            {outcome.busted && ` · ${dollars(outcome.pnl, true)}`}
            {outcome.closedAt !== null && ` · out at +${Math.floor(outcome.closedAt / POINTS_PER_MINUTE)} min`}
          </div>
          {best && best.pnl > 0n && (
            <div className="gm-best num">
              Best this hour: {best.side === "long" ? "long" : "short"} {outcome.leverage}×, out at +{Math.floor(best.exitAt / POINTS_PER_MINUTE)} min, {dollars(best.pnl, true)}
            </div>
          )}
          {round.reveal && <p className="gm-reveal">{round.reveal}</p>}
          {record && record.of >= 3 && (
            <p className="gm-archive">
              {round.category} headlines in the archive: {record.down} of {record.of} hours ended lower.
            </p>
          )}
          <div className="gm-result-actions">
            <button type="button" className="gm-next" onClick={() => onDone(outcome)} autoFocus>
              {last ? "Score" : "Next"}
            </button>
            {aside?.(outcome)}
          </div>
        </div>
      )}
      <Burst fire={burst.fire} tone={burst.tone} />
    </section>
  );
}

/** Hours that ended lower, by kind of news, over a pool of rounds. */
export function archiveOf(rounds: Round[]): Archive {
  const out: Archive = new Map();
  for (const round of rounds) {
    const { from, to } = hourOf(round);
    const held = out.get(round.category) ?? { down: 0, of: 0 };
    held.of += 1;
    if (to < from) held.down += 1;
    out.set(round.category, held);
  }
  return out;
}

// ---------------------------------------------------------------- the chart

/** An hour before the headline, an hour after; the after only as it plays. */
function Chart({ context, path, shown, entry, bust, side, winning }: { context: number[]; path: bigint[]; shown: number; entry: number; bust: number | null; side: Side | null; winning: boolean }) {
  const [frame, width] = useWidth<HTMLDivElement>();
  const height = width < 600 ? 180 : 220;
  const pad = { top: 16, bottom: 16, left: 0, right: 0 };
  const minutes = CONTEXT_MINUTES + REPLAY_MINUTES;
  const x = (minute: number) => pad.left + (minute / minutes) * (width - pad.left - pad.right);

  const revealed = useMemo(() => {
    if (shown < 0) return [] as number[];
    const at = Math.floor(shown);
    const out: number[] = [];
    for (let i = 0; i <= at && i < path.length; i++) out.push(toNumber(path[i] as bigint));
    const next = path[at + 1];
    if (next !== undefined && shown > at) {
      const a = out[out.length - 1] as number;
      out.push(a + (toNumber(next) - a) * (shown - at));
    }
    return out;
  }, [path, shown]);

  const values = [...context, entry, ...revealed];
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  // the bust is drawn when it is near; far away it is a label at the edge
  const bustNear = bust !== null && Math.abs(bust - entry) <= (hi - lo) * 1.2;
  if (bust !== null && bustNear) {
    lo = Math.min(lo, bust);
    hi = Math.max(hi, bust);
  }
  const span = hi - lo || entry * 0.001;
  lo -= span * 0.08;
  hi += span * 0.08;
  const y = (v: number) => pad.top + (1 - (v - lo) / (hi - lo)) * (height - pad.top - pad.bottom);

  const contextLine = context.map((v, i) => `${i ? "L" : "M"}${x(i + 1).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const replayLine = revealed.map((v, i) => `${i ? "L" : "M"}${x(CONTEXT_MINUTES + i / POINTS_PER_MINUTE).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const headX = x(CONTEXT_MINUTES);
  const pen = revealed.length ? { x: x(CONTEXT_MINUTES + (revealed.length - 1) / POINTS_PER_MINUTE), y: y(revealed[revealed.length - 1] as number) } : null;

  return (
    <div className="gm-chart" ref={frame}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="The hour before the headline, and the hour after as it plays">
        <rect className="gm-future" x={headX} y={0} width={width - headX} height={height} />
        <line className="gm-entry" x1={0} x2={width} y1={y(entry)} y2={y(entry)} />
        {bust !== null && bustNear && (
          <g className="gm-bust">
            <line x1={headX} x2={width} y1={y(bust)} y2={y(bust)} />
            <text x={width - 4} y={y(bust) + (side === "long" ? 14 : -6)} textAnchor="end">
              bust
            </text>
          </g>
        )}
        {bust !== null && !bustNear && (
          <text className="gm-bust-far" x={width - 4} y={side === "long" ? height - 4 : 12} textAnchor="end">
            bust {side === "long" ? "↓" : "↑"} {(Math.abs(bust / entry - 1) * 100).toFixed(1)}%
          </text>
        )}
        <line className="gm-now" x1={headX} x2={headX} y1={0} y2={height} />
        <path className="gm-context" d={contextLine} />
        {replayLine && <path className={`gm-line ${winning ? "up" : side ? "down" : ""}`} d={replayLine} />}
        {pen && <circle className={`gm-pen ${winning ? "up" : side ? "down" : ""}`} cx={pen.x} cy={pen.y} r={4} />}
        {!pen && <circle className="gm-pen" cx={headX} cy={y(entry)} r={4} />}
      </svg>
      <div className="gm-axis num">
        <span>−1 h</span>
        <span>headline</span>
        <span>+1 h</span>
      </div>
    </div>
  );
}
